import { OrderResultFinding, REPORT_TYPES_RISK_SCORE, REPORT_TYPES_SINGLE_GENE, ReportType } from '@customTypes/order';
import { Requisition } from '@customTypes/requisition';
import { isOrderReported } from '@helpers/order';
import { snakeToSentenceCase } from '@helpers/string';
import ResultsAverageSvg from '@statics/images/results-icons/results_average.svg?react';
import ResultsBorderLineSvg from '@statics/images/results-icons/results_borderline.svg?react';
import ResultsElevatedSvg from '@statics/images/results-icons/results_elevated-or-intermediate.svg?react';
import ResultsLowSvg from '@statics/images/results-icons/results_low.svg?react';
import ResultsUncertainSvg from '@statics/images/results-icons/results_uncertain.svg?react';

import { APOE_PRODUCT_IDS, PRS_PRODUCT_IDS, SINGLE_GENE_PRODUCT_IDS } from './Results.consts';
import { OrderType, ResultOrder } from './Results.types';

export const getUniqueResults = (requisitions: Requisition[]): ResultOrder[] => {
  const allCompletedOrders = requisitions
    .map(requisition => requisition.orders)
    .flat()
    .filter(isOrderReported);
  // Remove duplicated orders based on order_id and leave the most recent one
  const uniqueOrdersMap = new Map<string, ResultOrder>();
  allCompletedOrders.forEach(order => {
    if (!uniqueOrdersMap.has(order.order_id) || order.created_at > uniqueOrdersMap.get(order.order_id)!.created_at) {
      uniqueOrdersMap.set(order.order_id, order);
    }
  });
  // Add type
  return Array.from(uniqueOrdersMap.values()).map(order => ({
    ...order,
    type: getOrderType(order),
  }));
};

const reportTypeIsOneOf = (value: ResultOrder['report_type'], types: readonly ReportType[]): boolean =>
  value !== undefined && types.includes(value);

const isApoeOrder = (order: ResultOrder): boolean => APOE_PRODUCT_IDS.includes(order.product_id);

const getOrderType = (order: ResultOrder): OrderType => {
  // Check before other report types so an APOE add-on is not drawn as a
  // monogenic positive/negative or a medication-response card.
  if (isApoeOrder(order)) {
    return OrderType.APOE;
  }
  if (reportTypeIsOneOf(order.report_type, REPORT_TYPES_SINGLE_GENE)) {
    return OrderType.SINGLE_GENE;
  }
  if (reportTypeIsOneOf(order.report_type, REPORT_TYPES_RISK_SCORE)) {
    return OrderType.RISK_SCORE;
  }
  if (order.report_type === ReportType.PGX) {
    return OrderType.PGX;
  }

  // Fallback to legacy logic if report_type is not available
  if (SINGLE_GENE_PRODUCT_IDS.includes(order.product_id)) {
    return OrderType.SINGLE_GENE;
  }
  if (PRS_PRODUCT_IDS.includes(order.product_id)) {
    return OrderType.RISK_SCORE;
  }
  return OrderType.PGX;
};

const VARIANT_CLASSIFICATION_LABELS: Record<string, string> = {
  PATHOGENIC: 'Pathogenic',
  LIKELY_PATHOGENIC: 'Likely Pathogenic',
  VUS: 'Variant of Unknown Significance',
};

export const getVariantClassificationLabel = (classification?: string): string => {
  if (!classification) {
    return '';
  }
  return VARIANT_CLASSIFICATION_LABELS[classification] ?? snakeToSentenceCase(classification);
};

export const getRiskScoreIcon = (finding: OrderResultFinding) => {
  switch (finding) {
    case OrderResultFinding.NOT_ANALYZED:
    case OrderResultFinding.NOT_REPORTABLE:
      return ResultsUncertainSvg;
    case OrderResultFinding.LOW_RISK:
    case OrderResultFinding.NOT_INCREASED_RISK:
    case OrderResultFinding.DECREASED_RISK:
      return ResultsLowSvg;
    case OrderResultFinding.BORDERLINE_RISK:
      return ResultsBorderLineSvg;
    case OrderResultFinding.AVERAGE_RISK:
      return ResultsAverageSvg;
    case OrderResultFinding.HIGH_RISK:
    case OrderResultFinding.ELEVATED_RISK:
    case OrderResultFinding.INTERMEDIATE_RISK:
    case OrderResultFinding.INCREASED_RISK:
      return ResultsElevatedSvg;
    default:
      return ResultsElevatedSvg;
  }
};
